import React, { useState, useCallback, useMemo } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Menu, Dropdown, Button, Space, Drawer, Typography } from 'antd';
import type { MenuProps } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { MenuOutlined, UserOutlined } from '@ant-design/icons';
import { TEVService } from '../tevapi/io';
import { UserContextType } from './tev3';
import '../../styles/components/navbar.scss';

const { Text } = Typography;

interface NavbarProps {
  user: UserContextType;
}

interface NavItemProps {
  to: string;
  children: React.ReactNode;
  icon?: React.ReactNode;
}

// Custom NavLink component with active state styling
const NavItem: React.FC<NavItemProps> = ({ to, children, icon }) => {
  return (
    <NavLink
      to={to}
      className={({ isActive }) => `nav-item ${isActive ? 'nav-item-active' : ''}`}
    >
      {icon && <span className="nav-icon">{icon}</span>}
      {children}
    </NavLink>
  );
};

// Logout component with loading state
const LogoutButton: React.FC = () => {
  const [loading, setLoading] = useState(false);

  const handleLogout = useCallback(async () => {
    setLoading(true);
    try {
      // Call logout API
      await TEVService.post("/logout/", {});
      // Immediately refresh the page to clear all client-side state
      window.location.reload();
    } catch (error) {
      console.error('Logout error:', error);
      // Even if logout API fails, refresh the page to clear state
      window.location.reload();
    }
  }, []);

  return (
    <Button
      type="link"
      danger
      onClick={handleLogout}
      loading={loading}
      icon={<FontAwesomeIcon icon="fa-solid fa-arrow-right-from-bracket" />}
      className="logout-button"
    >
      Log Out
    </Button>
  );
};

// Account dropdown menu
const AccountMenu: React.FC<{ user: UserContextType }> = ({ user }) => {
  const dropdownItems: MenuProps['items'] = [
    {
      key: 'user-info',
      label: (
        <div className="user-info-dropdown">
          <Text strong>{user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : user.username}</Text>
          {user.email && <Text type="secondary" className="user-email">{user.email}</Text>}
        </div>
      ),
      disabled: true,
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      label: <LogoutButton />,
    },
  ];

  return (
    <Dropdown menu={{ items: dropdownItems }} placement="bottomRight" arrow>
      <Button type="text" className="account-button">
        <Space>
          <UserOutlined />
          <span className="account-label">Account</span>
        </Space>
      </Button>
    </Dropdown>
  );
};

// Main navigation bar component
export const Navbar: React.FC<NavbarProps> = ({ user }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  // Close mobile menu on route change
  React.useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  // Build menu items based on user authentication status
  const menuItems = useMemo(() => {
    const items: MenuProps['items'] = [
      {
        key: 'search',
        label: (
          <NavItem to="/search" icon={<FontAwesomeIcon icon="fa-regular fa-magnifying-glass" />}>
            Search
          </NavItem>
        ),
      },
      {
        key: 'help',
        label: (
          <NavItem to="/help" icon={<FontAwesomeIcon icon="fa-regular fa-square-question" />}>
            Help
          </NavItem>
        ),
      },
    ];

    // Add admin link for staff users
    if (user.is_staff) {
      items.push({
        key: 'admin',
        label: (
          <a href="/admin" className="nav-item">
            <span className="nav-icon">
              <FontAwesomeIcon icon="fa-regular fa-screwdriver-wrench" />
            </span>
            Administration
          </a>
        ),
      });
    }

    return items;
  }, [user.is_staff]);

  // Account menu or login link
  const accountElement = useMemo(() => {
    if (user.is_anonymous || !user.is_authenticated) {
      return (
        <NavItem to="/login" icon={<FontAwesomeIcon icon="fa-regular fa-arrow-right-to-bracket" />}>
          Login
        </NavItem>
      );
    }
    return <AccountMenu user={user} />;
  }, [user]);

  return (
    <>
      {/* Desktop Navigation */}
      <nav className="navbar-desktop">
        <Menu
          mode="horizontal"
          selectedKeys={[location.pathname]}
          items={menuItems}
          className="main-nav"
        />
        <div className="nav-right">
          {accountElement}
        </div>
      </nav>

      {/* Mobile Navigation */}
      <nav className="navbar-mobile">
        <Button
          type="text"
          icon={<MenuOutlined />}
          onClick={() => setMobileMenuOpen(true)}
          className="mobile-menu-trigger"
          aria-label="Open navigation menu"
        />

        <Drawer
          title="Navigation"
          placement="left"
          onClose={() => setMobileMenuOpen(false)}
          open={mobileMenuOpen}
          className="mobile-menu-drawer"
        >
          <Menu
            mode="vertical"
            selectedKeys={[location.pathname]}
            items={menuItems}
            className="mobile-nav"
            onClick={() => setMobileMenuOpen(false)}
          />
          <div className="mobile-account-section">
            {accountElement}
          </div>
        </Drawer>
      </nav>
    </>
  );
};

// Export the function that returns menu items for the layout component
export const TEVNavLinks = (user: UserContextType): MenuProps['items'] => {
  const items: MenuProps['items'] = [
    {
      key: 'search',
      icon: <FontAwesomeIcon icon="fa-regular fa-magnifying-glass" />,
      label: <NavLink to="/search">Search</NavLink>,
    },
    {
      key: 'help',
      icon: <FontAwesomeIcon icon="fa-regular fa-square-question" />,
      label: <NavLink to="/help">Help</NavLink>,
    },
  ];

  // Account menu
  if (user.is_anonymous || !user.is_authenticated) {
    items.push({
      key: 'login',
      icon: <FontAwesomeIcon icon="fa-regular fa-arrow-right-to-bracket" />,
      label: <NavLink to="/login">Login</NavLink>,
    });
  } else {
    items.push({
      key: 'account',
      label: 'Account',
      icon: <FontAwesomeIcon icon="fa-solid fa-user-gear" />,
      children: [
        {
          key: 'logout',
          label: <LogoutButton />,
        },
      ],
    });
  }

  // Admin link for staff
  if (user.is_staff) {
    items.push({
      key: 'admin',
      icon: <FontAwesomeIcon icon="fa-regular fa-screwdriver-wrench" />,
      label: <a href="/admin">Administration</a>,
    });
  }

  return items;
};
